(() => {
  const root = document.querySelector("[data-spec-finder]");
  if (!root || root.dataset.ready === "true") return;
  root.dataset.ready = "true";

  const catalogNode = root.querySelector("[data-spec-catalog]");
  const translationNode = root.querySelector("[data-spec-translations]");
  let products = [];
  let copy = {};
  try { products = JSON.parse(catalogNode?.textContent || "[]"); } catch (_) { products = []; }
  try { copy = JSON.parse(translationNode?.textContent || "{}"); } catch (_) { copy = {}; }

  const fields = ["diameter", "width", "pcd", "offset", "centerBore", "finish"];
  const selected = Object.fromEntries(fields.map((field) => [field, ""]));
  const variants = products.flatMap((product) => product.variants.map((variant) => ({
    ...variant,
    productId: product.id,
    productTitle: product.title,
    productHandle: product.handle,
    productUrl: product.url,
    productImage: product.image,
    productImageAlt: product.imageAlt,
  })));

  const form = root.querySelector("[data-spec-form]");
  const grid = root.querySelector("[data-spec-result-grid]");
  const empty = root.querySelector("[data-spec-empty]");
  const count = root.querySelector("[data-result-count]");
  const summary = root.querySelector("[data-spec-summary]");
  const submit = root.querySelector("[data-spec-submit]");
  const submitLabel = root.querySelector("[data-spec-submit-label]");
  const fitmentLink = root.querySelector("[data-spec-fitment-link]");
  const fieldRoots = Object.fromEntries(fields.map((field) => [field, root.querySelector(`[data-spec-field="${field}"]`)]));

  const normalize = (value) => String(value ?? "").trim();
  const matches = (variant, exceptField = "") => fields.every((field) => field === exceptField || !selected[field] || normalize(variant[field]) === selected[field]);
  const numeric = (value) => Number.parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  const valueSort = (field) => (a, b) => field === "finish" ? a.localeCompare(b) : (numeric(a) - numeric(b) || a.localeCompare(b));
  const valuesFor = (field) => [...new Set(variants.filter((variant) => variant.available && matches(variant, field)).map((variant) => normalize(variant[field])).filter(Boolean))].sort(valueSort(field));
  const allValuesFor = (field) => [...new Set(variants.map((variant) => normalize(variant[field])).filter(Boolean))].sort(valueSort(field));
  const activeCount = () => fields.filter((field) => selected[field]).length;
  const resultSet = () => variants.filter((variant) => variant.available && matches(variant));
  const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]);

  const labelValue = (field, value) => {
    if (!value) return copy.any || "Any";
    if (field === "diameter" || field === "width") return `${value}”`;
    if (field === "offset") return `ET${value}`;
    if (field === "centerBore") return `CB${value}`;
    return value;
  };

  const pickImage = (variant) => ({ url: variant.productImage, alt: variant.productImageAlt });

  const updateQuery = () => {
    const params = new URLSearchParams(window.location.search);
    fields.forEach((field) => selected[field] ? params.set(field, selected[field]) : params.delete(field));
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`;
    window.history.replaceState({}, "", next);
  };

  const renderControls = () => {
    fields.forEach((field) => {
      const optionRoot = fieldRoots[field]?.querySelector("[data-spec-options]");
      if (!optionRoot) return;
      const validValues = new Set(valuesFor(field));
      optionRoot.innerHTML = allValuesFor(field).map((value) => {
        const active = selected[field] === value;
        const valid = validValues.has(value);
        return `<button type="button" data-spec-value="${escapeHtml(value)}" aria-pressed="${active}" ${valid ? "" : "disabled"}><span>${escapeHtml(labelValue(field, value))}</span><i aria-hidden="true"></i></button>`;
      }).join("");
      fieldRoots[field].classList.toggle("has-value", Boolean(selected[field]));
    });
  };

  const renderSummary = (results) => {
    const tokens = fields.filter((field) => selected[field]).map((field) => labelValue(field, selected[field]));
    summary.textContent = tokens.length ? tokens.join(" / ") : (copy.noSelection || "No specification selected");
    submit.disabled = !tokens.length || !results.length;
    submitLabel.textContent = tokens.length
      ? `${copy.viewMatches || "View matches"} · ${results.length}`
      : (copy.selectToBegin || "Select a specification to begin");

    const fitmentParams = new URLSearchParams();
    if (tokens.length) fitmentParams.set("wheel_spec", tokens.join(" / "));
    fitmentLink.href = `${root.dataset.fitmentUrl}${fitmentParams.toString() ? `?${fitmentParams}` : ""}`;
  };

  const resultCard = (variant, index) => {
    const image = pickImage(variant);
    const url = `${variant.productUrl}?variant=${encodeURIComponent(variant.id)}`;
    return `<article class="spec-result-card" style="--result-index:${index}">
      <a class="spec-result-card__media" href="${escapeHtml(url)}">
        ${image.url ? `<img src="${escapeHtml(image.url)}" alt="${escapeHtml(image.alt || variant.productTitle)}" width="900" height="900" loading="lazy">` : ""}
        <span>${escapeHtml(copy.exactSku || "Exact SKU")}</span>
      </a>
      <div class="spec-result-card__copy">
        <div class="spec-result-card__top"><small>${escapeHtml(variant.sku)}</small><span><i></i>${escapeHtml(copy.madeToOrder || "MADE TO ORDER · REVIEW REQUIRED")}</span></div>
        <h3><a href="${escapeHtml(url)}">${escapeHtml(variant.productTitle)}</a></h3>
        <strong class="spec-result-card__size">${escapeHtml(variant.size)}</strong>
        <dl>
          <div><dt>PCD</dt><dd>${escapeHtml(variant.pcd)}</dd></div>
          <div><dt>${escapeHtml(copy.offset || "Offset")}</dt><dd>ET${escapeHtml(variant.offset)}</dd></div>
          <div><dt>${escapeHtml(copy.centerBore || "Center bore")}</dt><dd>CB${escapeHtml(variant.centerBore)}</dd></div>
          <div><dt>${escapeHtml(copy.finish || "Finish")}</dt><dd>${escapeHtml(variant.finish)}</dd></div>
        </dl>
        <div class="spec-result-card__buy"><p><strong>${escapeHtml(variant.price)}</strong><small>${escapeHtml(copy.perWheel || "per wheel")} · ${escapeHtml(copy.approvalLeadTime || "7–15 days after approval")}</small></p><a href="${escapeHtml(url)}">${escapeHtml(copy.viewConfiguration || "View configuration")} <span>→</span></a></div>
      </div>
    </article>`;
  };

  const renderResults = (scroll = false) => {
    const results = resultSet().sort((a, b) => numeric(a.diameter) - numeric(b.diameter) || numeric(a.width) - numeric(b.width) || a.productTitle.localeCompare(b.productTitle) || numeric(a.priceValue) - numeric(b.priceValue));
    count.textContent = activeCount() ? String(results.length) : "0";
    grid.innerHTML = activeCount() ? results.map(resultCard).join("") : "";
    empty.hidden = Boolean(activeCount() && results.length);
    if (activeCount() && !results.length) {
      empty.querySelector("h3").textContent = copy.noMatchesHeading || "No exact configuration found";
      empty.querySelector("p").textContent = copy.noMatchesBody || "Remove one specification or begin with the vehicle finder.";
    }
    renderSummary(results);
    if (scroll) root.querySelector("[data-spec-results]")?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
  };

  const applySelection = (field, value) => {
    selected[field] = selected[field] === value ? "" : value;
    fields.forEach((otherField) => {
      if (selected[otherField] && !valuesFor(otherField).includes(selected[otherField])) selected[otherField] = "";
    });
    renderControls();
    renderResults();
    updateQuery();
  };

  fields.forEach((field) => {
    fieldRoots[field]?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-spec-value]");
      if (!button || button.disabled) return;
      applySelection(field, button.dataset.specValue || "");
    });
  });

  form?.addEventListener("submit", (event) => { event.preventDefault(); renderResults(true); });
  form?.addEventListener("reset", (event) => {
    event.preventDefault();
    fields.forEach((field) => { selected[field] = ""; });
    renderControls();
    renderResults();
    updateQuery();
  });

  const params = new URLSearchParams(window.location.search);
  fields.forEach((field) => {
    const value = normalize(params.get(field));
    if (value && allValuesFor(field).includes(value)) selected[field] = value;
  });
  fields.forEach((field) => {
    if (selected[field] && !valuesFor(field).includes(selected[field])) selected[field] = "";
  });
  renderControls();
  renderResults();
})();
